import { Route, Routes } from 'react-router-dom'

import { RequireRole } from './auth/RequireRole'
import { AppShell } from './components/AppShell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AccountLifecyclePage } from './pages/AccountLifecyclePage'
import { AuditLogPage } from './pages/AuditLogPage'
import { DaemonInspectorPage } from './pages/DaemonInspectorPage'
import { DeathConfirmationListPage } from './pages/DeathConfirmationListPage'
import { DeliveryDetailPage } from './pages/DeliveryDetailPage'
import { DeliveryProblemListPage } from './pages/DeliveryProblemListPage'
import { HealthPage } from './pages/HealthPage'
import { LoginPage } from './pages/LoginPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { OperatorManagementPage } from './pages/OperatorManagementPage'
import { PhysicalMailDispatchPage } from './pages/PhysicalMailDispatchPage'
import { PhysicalMailListPage } from './pages/PhysicalMailListPage'
import { UserDetailPage } from './pages/UserDetailPage'
import type { OperatorRole } from './api/types'

// All non-/login routes live inside <AppShell>. Most pages are
// VIEWER-readable (mutating actions on those pages role-gate
// individual buttons); the Daemon Inspector requires OPERATOR for
// the whole page because the trigger buttons are its raison d'être.
function ShelledRoute(
  { children, role = 'VIEWER' }:
  { children: React.ReactNode; role?: OperatorRole },
) {
  return (
    <RequireRole role={role}>
      <AppShell>
        <ErrorBoundary>{children}</ErrorBoundary>
      </AppShell>
    </RequireRole>
  )
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={<ShelledRoute><HealthPage /></ShelledRoute>}
      />
      <Route
        path="/death-confirmation"
        element={
          <ShelledRoute>
            <DeathConfirmationListPage />
          </ShelledRoute>
        }
      />
      <Route
        path="/death-confirmation/:linkAccount"
        element={
          <ShelledRoute>
            <UserDetailPage />
          </ShelledRoute>
        }
      />
      <Route
        path="/deliveries"
        element={
          <ShelledRoute>
            <DeliveryProblemListPage />
          </ShelledRoute>
        }
      />
      <Route
        path="/deliveries/:deliveryId"
        element={
          <ShelledRoute>
            <DeliveryDetailPage />
          </ShelledRoute>
        }
      />
      <Route
        path="/physical-mail"
        element={
          <ShelledRoute>
            <PhysicalMailListPage />
          </ShelledRoute>
        }
      />
      <Route
        path="/physical-mail/:messageId"
        element={
          <ShelledRoute>
            <PhysicalMailDispatchPage />
          </ShelledRoute>
        }
      />
      <Route
        path="/lifecycle"
        element={
          <ShelledRoute>
            <AccountLifecyclePage />
          </ShelledRoute>
        }
      />
      <Route
        path="/audit"
        element={<ShelledRoute><AuditLogPage /></ShelledRoute>}
      />
      <Route
        path="/daemon"
        element={
          <ShelledRoute role="OPERATOR">
            <DaemonInspectorPage />
          </ShelledRoute>
        }
      />
      <Route
        path="/operators"
        element={
          <ShelledRoute role="ADMIN">
            <OperatorManagementPage />
          </ShelledRoute>
        }
      />
      <Route
        path="*"
        element={<ShelledRoute><NotFoundPage /></ShelledRoute>}
      />
    </Routes>
  )
}
